import { defineModule } from '@/js/utils/helpers';
import { Overlay, toggleOverlay } from '@/js/utils/overlays';

/**
 * The menu panel clips open from the header's language/menu bar and retracts
 * into it — `--ip-menu-clip` in css/theme/layout.css is that bar's rect, and the
 * keyframes in motion.css grow the panel from it. The panel is `hidden` while
 * closed so nothing under it is focusable, which means the open class has to be
 * applied after the element is displayed, and removed only once the closing
 * animation has run.
 *
 * While it is open the panel holds the focus: Tab cycles between the header
 * bar's own controls and the panel's links, the rest of the page is `inert`, and
 * closing hands focus back to whatever opened it. Escape, the toggle and a press
 * outside the surface all close it.
 */

const OPEN_CLASS = 'ip-menu-in';
const CLOSE_CLASS = 'ip-menu-out';
const BODY_CLASS = 'ip-menu-open';
const CLOSE_DURATION = 260;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Read a layout property to force a style recalc before an animation class lands. */
const forceReflow = (element: HTMLElement) =>
  element.getBoundingClientRect().width;

let panel: HTMLElement | null = null;
let toggles: HTMLElement[] = [];
let closeTimer: ReturnType<typeof setTimeout> | null = null;
let returnFocusTo: HTMLElement | null = null;

const isOpen = () => document.body.classList.contains(BODY_CLASS);

/** The rest of the page, which `inert` takes out while the panel is open. */
const backdrop = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      '.page-wrapper > main, .page-wrapper > footer',
    ),
  );

const focusableIn = (root: HTMLElement | null) =>
  Array.from(root?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
    (element) => element.offsetParent !== null,
  );

const panelTabbable = () => focusableIn(panel);

/* Tab order for the trap. The header bar leads, because it sits on top of the
   panel as its close control, and it holds more than the toggle: the language
   switch opens independently of the menu (js/components/language-switch.ts), so
   its trigger and its open dropdown belong inside the trap rather than being
   pulled back into the panel on Tab. */
const barTabbable = () =>
  toggles.flatMap((toggle) => {
    const bar = toggle.closest<HTMLElement>('.ip-hdr-bar');
    return bar ? focusableIn(bar) : [toggle];
  });

const tabbable = () => [...new Set([...barTabbable(), ...panelTabbable()])];

const setExpanded = (expanded: boolean) =>
  toggles.forEach((toggle) =>
    toggle.setAttribute('aria-expanded', String(expanded)),
  );

const open = () => {
  if (!panel || isOpen()) return;

  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  panel.hidden = false;
  panel.classList.remove(CLOSE_CLASS);
  // The browser has to see the clipped start state before the animation class
  // lands, otherwise the panel simply appears.
  forceReflow(panel);
  panel.classList.add(OPEN_CLASS);

  document.body.classList.add(BODY_CLASS);
  toggleOverlay(Overlay.MENU, true);
  setExpanded(true);

  returnFocusTo = document.activeElement as HTMLElement | null;
  backdrop().forEach((element) => element.setAttribute('inert', ''));
  panelTabbable()[0]?.focus();
};

const close = () => {
  if (!panel || !isOpen()) return;

  panel.classList.remove(OPEN_CLASS);
  panel.classList.add(CLOSE_CLASS);
  document.body.classList.remove(BODY_CLASS);
  toggleOverlay(Overlay.MENU, false);
  setExpanded(false);

  backdrop().forEach((element) => element.removeAttribute('inert'));
  (returnFocusTo ?? toggles[0])?.focus();
  returnFocusTo = null;

  closeTimer = setTimeout(() => {
    if (!panel) return;
    panel.classList.remove(CLOSE_CLASS);
    panel.hidden = true;
    closeTimer = null;
  }, CLOSE_DURATION);
};

const onToggleClick = (event: Event) => {
  event.preventDefault();
  if (isOpen()) close();
  else open();
};

/* Capture, and pointerdown rather than click: the backdrop is `inert` while the
   panel is open, so a click there is never dispatched to it. The header bar is
   excluded because its toggle closes on click — closing here first would let
   that click reopen the panel. */
const onPointerDown = (event: Event) => {
  if (!isOpen()) return;

  const target = event.target as Element | null;
  if (target?.closest('.ip-menu, .ip-hdr-bar')) return;

  close();
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    close();
    return;
  }

  if (event.key !== 'Tab' || !isOpen()) return;

  const items = tabbable();
  if (!items.length) return;

  const active = document.activeElement as HTMLElement | null;
  const index = active ? items.indexOf(active) : -1;
  const wrapping = event.shiftKey ? index <= 0 : index === items.length - 1;

  if (!wrapping && index !== -1) return;

  event.preventDefault();
  items[event.shiftKey ? items.length - 1 : 0].focus();
};

export default defineModule(
  () => {
    panel = document.querySelector<HTMLElement>('[data-menu]');
    if (!panel) return;

    toggles = Array.from(
      document.querySelectorAll<HTMLElement>('[data-menu-toggle]'),
    );
    toggles.forEach((toggle) =>
      toggle.addEventListener('click', onToggleClick),
    );
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('pointerdown', onPointerDown, true);
  },
  () => {
    toggles.forEach((toggle) =>
      toggle.removeEventListener('click', onToggleClick),
    );
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onPointerDown, true);

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (isOpen()) {
      document.body.classList.remove(BODY_CLASS);
      toggleOverlay(Overlay.MENU, false);
      backdrop().forEach((element) => element.removeAttribute('inert'));
    }

    returnFocusTo = null;

    panel = null;
    toggles = [];
  },
);
